import loadingStyle from "./components/Common/loading.module.css";

import Header from "./components/Header/Header.tsx";
import { Outlet } from "react-router-dom";
import {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";

import "./App.css";

import { AuthContext } from "./contexts/AuthContext.ts";
import { useAuth0 } from "@auth0/auth0-react";
import { checkTokenExpired, getTokenFromStorage } from "./tokenFunctions.ts";
import useAxiosInterceptor from "./hooks/useAxiosInterceptor.tsx";
import { AppContext, ModalProps, ModalType } from "./contexts/AppContext.ts";

import logo from "./assets/PentraLogoDark.svg";
import loading from "./assets/loading.json";
import { Player } from "@lottiefiles/react-lottie-player";
import ConfirmModal, {
  ConfirmModalData,
} from "./components/Common/Modals/ConfirmModal/ConfirmModal.tsx";
import LoggedOut from "./components/LoginChecks/LoggedOut/LoggedOut.tsx";
import AlertManager, {
  AlertBlock,
  AlertData,
} from "./components/Common/AlertManager/AlertManager.tsx";
import InfoPrompt from "./components/LoginChecks/InfoPrompt/InfoPrompt.tsx";
import VerifyEmail from "./components/LoginChecks/VerifyEmail/VerifyEmail.tsx";
import GenericModal, {
  GenericModalData,
} from "./components/Common/Modals/GenericModal/GenericModal.tsx";

import { TutorialContext } from "./contexts/TutorialContext.ts";
import { Tutorial } from "./components/LoginChecks/Tutorial/Tutorial.tsx";
import NameModal, {
  NameModalData,
} from "./components/Common/Modals/NameModal/NameModal.tsx";
import { apiGetTutorialStep, apiIncrementTutorialStep } from "./sdk.ts";
import InstallationGuide from "./components/LoginChecks/InstallationGuide/InstallationGuide.tsx";

import { useSearchParams } from "react-router-dom";
import PendingApproval from "./components/LoginChecks/PendingApproval/PendingApproval.tsx";

function sendTokenToServiceWorker(token: string) {
  if (!("serviceWorker" in navigator)) {
    console.error("Service workers are not supported in this browser.");
    return;
  }

  const sendToken = (registration: ServiceWorkerRegistration) => {
    if (registration.active) {
      registration.active.postMessage({ type: "SET_TOKEN", token: token });
      console.log("Token sent to service worker");
    } else {
      console.error("No active service worker found.");
    }
  };

  if (navigator.serviceWorker.controller) {
    // Verifica daca e un service worker pt pagina
    navigator.serviceWorker.ready.then(sendToken);
  } else {
    const listener = () => {
      navigator.serviceWorker.ready.then(sendToken);
      navigator.serviceWorker.removeEventListener("controllerchange", listener);
    };
    navigator.serviceWorker.addEventListener("controllerchange", listener); // Listener pana cad e disponibil ca sa nu se trimita tokenul inainte
  }
}

export function AuthContextProvider(
  props: Readonly<{ children: JSX.Element }>
) {
  const [token, setToken] = useState("");
  const { isAuthenticated, logout } = useAuth0();

  useEffect(() => {
    const tokenFromStorage = getTokenFromStorage();
    if (!tokenFromStorage) return;
    if (checkTokenExpired(tokenFromStorage)) {
      console.log("Expired token!");
      sessionStorage.clear();
      logout({ logoutParams: { returnTo: window.location.origin } });
      return;
    }
    setToken(tokenFromStorage);
    sendTokenToServiceWorker(tokenFromStorage);
  }, [isAuthenticated, logout]);

  const memo_token = useMemo(() => {
    return { token };
  }, [token]);
  return (
    <AuthContext.Provider value={memo_token}>
      {props.children}
    </AuthContext.Provider>
  );
}

export function AppContextProvider(props: Readonly<{ children: JSX.Element }>) {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState<ModalProps>(undefined);
  const [showModalType, setShowModalType] = useState<ModalType>("generic");

  const [alerts, setAlerts] = useState<AlertBlock[]>([]);
  const [count, setCount] = useState<number>(0);
  const [tutorial, setTutorial] = useState<boolean>(false);

  const removeAlert = useCallback((id: number) => {
    setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id));
  }, []);
  const pushAlert = useCallback(
    (value: AlertData) => {
      setAlerts((prevAlerts) => [...prevAlerts, { data: value, id: count }]);
      if (value.time) setTimeout(() => removeAlert(count), value.time);

      setCount((prevCount) => prevCount + 1);
    },
    [count, removeAlert]
  );

  const appValue = useMemo(
    () => ({
      loading,
      setLoading,
      showModal,
      showModalType,
      setShowModal: (type: ModalType, value: ModalProps) => {
        setShowModalType(type);
        setShowModal(value);
      },
      alerts,
      pushAlert,
      removeAlert,
      tutorial,
      setTutorial,
    }),
    [
      loading,
      showModal,
      pushAlert,
      alerts,
      removeAlert,
      tutorial,
      showModalType,
    ]
  );

  return (
    <AppContext.Provider value={appValue}>{props.children}</AppContext.Provider>
  );
}

function getLoadingScreen() {
  return (
    <div id="loading-wrapper" className={loadingStyle.wrapper}>
      <img alt="logo" className={loadingStyle.logo} src={logo} />
      <Player autoplay loop src={loading} className={loadingStyle.lottie} />
    </div>
  );
}

function noLoginRequired() {
  if (
    window.location.pathname.includes("/downloads") ||
    window.location.pathname.includes("/button-styles")
  )
    return true;
  return false;
}

export function TutorialContextProvider(
  props: Readonly<{ children: JSX.Element }>
) {
  const helpButtonRef = useRef(null);
  const newPentestButtonRef = useRef(null);
  const newPentestFormRef = useRef(null);

  const sidebarRef = useRef(null);
  const tokenBoxRef = useRef(null);
  const searchListRef = useRef(null);
  const refreshListRef = useRef(null);
  const toggleTokenRef = useRef(null);
  const editorButtonRef = useRef(null);
  const exportPdfRef = useRef(null);

  const [stepIndex, setStepIndex] = useState(0);

  const contextValue = useMemo(
    () => ({
      tutorialRefs: {
        helpButtonRef,
        newPentestButtonRef,
        newPentestFormRef,
        sidebarRef,
        tokenBoxRef,
        searchListRef,
        refreshListRef,
        toggleTokenRef,
        editorButtonRef,
        exportPdfRef,
      },
      stepIndex,
      setStepIndex: (value: number) => {
        setStepIndex(value);
      },
      nextStep: () => {
        setTimeout(() => {
          setStepIndex(stepIndex + 1);
        }, 100);
      },
    }),
    [stepIndex]
  );
  return (
    <TutorialContext.Provider value={contextValue}>
      {props.children}
    </TutorialContext.Provider>
  );
}

function App() {
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  const errorDescParam = searchParams.get("error_description");

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then(() => {
        console.log("Service worker is ready");
      });

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        console.log("Service worker controller changed");
      });
    }
  }, []);

  const { showModal, showModalType, loading, setTutorial } =
    useContext(AppContext);
  const { token } = useContext(AuthContext);

  function getModal() {
    if (!showModal || showModalType === "none") return;
    if (showModalType === "confirm")
      return <ConfirmModal {...(showModal as ConfirmModalData)} />;
    else if (showModalType === "name")
      return <NameModal {...(showModal as NameModalData)} />;
    return <GenericModal {...(showModal as GenericModalData)} />;
  }

  const [tutorialStep, setTutorialStep] = useState(-1);

  /*
   * 0 - Verificare mail (daca username nu este PentraTest)
   * 1 - Form pentru contact information
   * 2 - Installation tutorial pentru CLI logger
   * 3 - Load la platforma si inceput Joyride
   */
  async function nextTutorialStep() {
    setTutorialStep((value) => value + 1);

    const response = await apiIncrementTutorialStep(token);
    if (response.data.step === 3) setTutorial(true);
  }

  useEffect(() => {
    async function load(retries = 0) {
      try {
        const response = await apiGetTutorialStep(token);
        setTutorialStep(response.data.step);

        if (response.data.step === 3) {
          setTutorial(true);
        }
      } catch (error) {
        console.error("Error loading tutorial step:", error);

        if (retries < 6) {
          // Limit retries to prevent infinite loops
          setTimeout(() => load(retries + 1), 500);
          console.log("Retrying ...");
        }
      }
    }

    if (token) load();
  }, [token, setTutorial]);

  useAxiosInterceptor();
  if (
    errorParam == "access_denied" &&
    errorDescParam == "Your account has not been approved yet."
  ) {
    return (
      <>
        <Header />
        <PendingApproval />
      </>
    );
  } else {
    if (!(token || loading || noLoginRequired()))
      return (
        <>
          <Header />
          <LoggedOut />
        </>
      );
    else if (token && tutorialStep === -1) return;
    else if (token && tutorialStep === 0) {
      return (
        <>
          <Header />
          <VerifyEmail advance={nextTutorialStep} />
        </>
      );
    } else if (token && tutorialStep === 1) {
      return (
        <>
          <Header />
          <InfoPrompt advance={nextTutorialStep} />
        </>
      );
    } else if (token && tutorialStep === 2) {
      return (
        <>
          <Header />
          <InstallationGuide advance={nextTutorialStep} />
        </>
      );
    }
  }

  return (
    <>
      <Header />
      <Outlet />
      {loading && getLoadingScreen()}
      {getModal()}
      <AlertManager />
      <Tutorial
        advance={() => {
          if (tutorialStep === 3) nextTutorialStep();
        }}
      />
    </>
  );
}
export default App;
